import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Spinner from '../_common/Spinner';
import {
    getDrainStationAiValidationSession,
    getDrainWaterStationHistory,
    startDrainStationAiValidation
} from '../../store/actions/drainStationsActions';
import './DrainWaterStationHistory.scss';

const formatLocalDate = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseLocalDate = value => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
};

const displayValue = value => value === null || value === undefined ? '-' : value;

const displayAiValue = value => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
};

const POLLING_INTERVAL = 5000;
const DEFAULT_AI_DAYS_BACK = 14;
const FINISHED_AI_STATUSES = ['COMPLETED', 'FAILED'];
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const firstPresentValue = (...values) => values.find(value => value !== null && value !== undefined && value !== '');

const resolveDrainStationId = station => firstPresentValue(
    station.id,
    station.drainStationId,
    station.drainWaterStationId,
    station.stationId,
    station.inWaterCountSensorId
);

const calendarDaysBetween = (dateFrom, dateTo) => {
    const fromUtc = Date.UTC(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate());
    const toUtc = Date.UTC(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate());
    return Math.max(1, Math.round((toUtc - fromUtc) / MILLISECONDS_PER_DAY));
};

const formatCycleTime = value => {
    if (!value) return '-';
    const match = String(value).match(/T(\d{2}:\d{2})/);
    return match ? match[1] : '-';
};

const cycleStartedBeforeIn = cycle => Boolean(
    cycle.outCycleStartedBeforeInCycle || cycle.outCycleStartedBeforInCycle
);

const cycleHasWarning = cycle => (
    cycleStartedBeforeIn(cycle) || Boolean(cycle.outCycleFinishedBeforeInCycle)
);

const CycleFlagIcon = ({warning, label}) => (
    <span
        className={`material-icons drainCycleFlag ${warning ? 'drainCycleFlag--warning' : 'drainCycleFlag--ok'}`}
        title={label}
        aria-label={label}
    >
        {warning ? 'error' : 'check_circle'}
    </span>
);

const CycleTimingHeaderIcon = ({type, label}) => {
    const startsBefore = type === 'startsBefore';
    return (
        <span className="drainCycleTimingHeaderIcon" title={label} aria-label={label}>
            <svg width="72" height="42" viewBox="0 0 160 92" xmlns="http://www.w3.org/2000/svg"
                 role="img" aria-hidden="true">
                <polygon
                    points={startsBefore ? '30,10 118,10 138,28 118,46 30,46' : '10,10 118,10 138,28 118,46 10,46'}
                    fill="#2F7FE0"
                />
                <text x={startsBefore ? '76' : '66'} y="33" textAnchor="middle"
                      fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="18"
                      fill="#ffffff" letterSpacing="1">IN</text>
                <polygon
                    points={startsBefore ? '10,52 118,52 138,70 118,88 10,88' : '10,52 88,52 108,70 88,88 10,88'}
                    fill="#E14336"
                />
                <text x={startsBefore ? '66' : '54'} y="75" textAnchor="middle"
                      fontFamily="Arial, Helvetica, sans-serif" fontWeight="700" fontSize="18"
                      fill="#ffffff" letterSpacing="1">OUT</text>
            </svg>
        </span>
    );
};

const buildAnalysisRequest = (station, dateFrom, dateTo) => {
    const drainStationId = resolveDrainStationId(station);
    const request = {
        drainStationId,
        daysBack: dateFrom && dateTo
            ? calendarDaysBetween(dateFrom, dateTo)
            : DEFAULT_AI_DAYS_BACK
    };

    if (station.inWaterCountSensorId && station.inWaterCountSensorId !== drainStationId) {
        request.stationId = station.inWaterCountSensorId;
    }

    if (dateTo) {
        request.dateTo = formatLocalDate(dateTo);
    }

    return request;
};

const isFinishedAiSession = status => FINISHED_AI_STATUSES.includes(status);

const normalizeList = value => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
};

const parseIssues = value => {
    if (!value) return [];

    if (typeof value === 'string') {
        try {
            const parsedValue = JSON.parse(value);
            return normalizeList(parsedValue.issues || parsedValue);
        } catch (error) {
            return [value];
        }
    }

    return normalizeList(value.issues || value);
};

const parseObject = value => {
    if (!value || typeof value !== 'string') return value || {};

    try {
        return JSON.parse(value);
    } catch (error) {
        return {};
    }
};

const renderList = (items, emptyText) => {
    const normalizedItems = normalizeList(items);
    if (!normalizedItems.length) {
        return <div className="drainAiMuted">{emptyText}</div>;
    }

    return (
        <ul className="drainAiList">
            {normalizedItems.map((item, index) => <li key={index}>{displayAiValue(item)}</li>)}
        </ul>
    );
};

const renderIssues = (issues, emptyText, t) => {
    const normalizedIssues = parseIssues(issues);
    if (!normalizedIssues.length) {
        return <div className="drainAiMuted">{emptyText}</div>;
    }

    return (
        <div className="drainAiIssues">
            {normalizedIssues.map((issue, index) => {
                if (!issue || typeof issue !== 'object') {
                    return <div className="drainAiIssue" key={index}>{displayAiValue(issue)}</div>;
                }

                const severity = typeof issue.severity === 'string' ? issue.severity.toUpperCase() : '';
                const severityClass = ['WARNING', 'HIGH', 'INFO'].includes(severity)
                    ? severity.toLowerCase()
                    : 'other';

                return (
                    <article className="drainAiIssue" key={issue.code || index}>
                        <div className="drainAiIssueHeader">
                            <span className={`drainAiIssueCode drainAiIssueCode--${severityClass}`}>
                                {displayAiValue(issue.code)}
                            </span>
                        </div>
                        <div className="drainAiIssueField">
                            <strong>{t.gettext('Message')}</strong>
                            <p>{displayAiValue(issue.message)}</p>
                        </div>
                        <div className="drainAiIssueField drainAiIssueEvidence">
                            <strong>{t.gettext('Evidence')}</strong>
                            <p>{displayAiValue(issue.evidence)}</p>
                        </div>
                    </article>
                );
            })}
        </div>
    );
};

const renderAssessment = (assessmentValue, t) => {
    const assessment = parseObject(assessmentValue);
    const sensors = ['in', 'out'].filter(sensorKey => assessment[sensorKey]);
    if (!sensors.length) return null;

    return (
        <section className="drainAiSection drainAiAssessmentSection">
            <h4>{t.gettext('Sensor assessment')}</h4>
            <div className="drainAiAssessmentGrid">
                {sensors.map(sensorKey => {
                    const sensor = parseObject(assessment[sensorKey]);
                    const status = typeof sensor.status === 'string' ? sensor.status.toUpperCase() : '';
                    const statusClass = ['GOOD', 'DEGRADED'].includes(status) ? status.toLowerCase() : 'other';

                    return (
                        <article className="drainAiAssessmentItem" key={sensorKey}>
                            <div className="drainAiAssessmentHeader">
                                <strong>{sensorKey === 'in' ? t.gettext('IN sensor') : t.gettext('OUT sensor')}</strong>
                                <span className={`drainAiSensorStatus drainAiSensorStatus--${statusClass}`}>
                                    {displayAiValue(status)}
                                </span>
                            </div>
                            <p>{displayAiValue(sensor.summary)}</p>
                        </article>
                    );
                })}
            </div>
        </section>
    );
};

const DrainWaterStationHistory = ({station, onBack, onClose}) => {
    const t = useSelector(state => state.language.translator);
    const [report, setReport] = useState(null);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [pending, setPending] = useState(true);
    const [error, setError] = useState('');
    const [expandedDays, setExpandedDays] = useState({});
    const [aiSession, setAiSession] = useState(null);
    const [aiPending, setAiPending] = useState(false);
    const [aiError, setAiError] = useState('');
    const aiPollingTimeout = useRef(null);
    const aiPollingController = useRef(null);
    const initialRequest = useRef(true);

    const loadReport = useCallback((from, to) => {
        const controller = new AbortController();
        setPending(true);
        setError('');
        getDrainWaterStationHistory(
            resolveDrainStationId(station),
            from ? formatLocalDate(from) : null,
            to ? formatLocalDate(to) : null,
            controller.signal
        )
            .then(data => {
                setReport(data);
                if (!from && !to) {
                    setDateFrom(parseLocalDate(data.dateFrom));
                    setDateTo(parseLocalDate(data.dateTo));
                }
            })
            .catch(requestError => {
                if (requestError.name !== 'AbortError') {
                    setError(t.gettext('Drain water station history could not be loaded.'));
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setPending(false);
            });
        return controller;
    }, [station, t]);

    useEffect(() => {
        const controller = loadReport(null, null);
        return () => controller.abort();
    }, [loadReport]);

    useEffect(() => {
        if (initialRequest.current) {
            if (dateFrom && dateTo) initialRequest.current = false;
            return undefined;
        }
        if (!dateFrom || !dateTo || dateFrom > dateTo) return undefined;
        const controller = loadReport(dateFrom, dateTo);
        return () => controller.abort();
    }, [dateFrom, dateTo, loadReport]);

    const clearAiPolling = useCallback(() => {
        if (aiPollingTimeout.current) {
            clearTimeout(aiPollingTimeout.current);
            aiPollingTimeout.current = null;
        }
        if (aiPollingController.current) {
            aiPollingController.current.abort();
            aiPollingController.current = null;
        }
    }, []);

    const loadAiSession = useCallback((sessionId, shouldContinuePolling = true) => {
        clearAiPolling();
        const controller = new AbortController();
        aiPollingController.current = controller;

        return getDrainStationAiValidationSession(sessionId, controller.signal)
            .then(data => {
                setAiSession(data);
                setAiError('');
                const finished = isFinishedAiSession(data.status);
                setAiPending(!finished);

                if (!finished && shouldContinuePolling) {
                    aiPollingTimeout.current = setTimeout(() => {
                        loadAiSession(sessionId, true);
                    }, POLLING_INTERVAL);
                }
            })
            .catch(requestError => {
                if (requestError.name !== 'AbortError') {
                    setAiPending(false);
                    setAiError(requestError.message || t.gettext('AI analysis status could not be loaded.'));
                }
            })
            .finally(() => {
                if (aiPollingController.current === controller) {
                    aiPollingController.current = null;
                }
            });
    }, [clearAiPolling, t]);

    useEffect(() => () => clearAiPolling(), [clearAiPolling]);

    const startAiAnalysis = () => {
        clearAiPolling();
        const controller = new AbortController();
        const request = buildAnalysisRequest(station, dateFrom, dateTo);
        if (!request.drainStationId) {
            setAiError(t.gettext('Drain station identifier is missing.'));
            return;
        }

        setAiPending(true);
        setAiError('');
        setAiSession(null);

        startDrainStationAiValidation(request, controller.signal)
            .then(data => {
                setAiSession(data);
                if (data.sessionId) {
                    return loadAiSession(data.sessionId, true);
                }
                setAiPending(false);
                return null;
            })
            .catch(requestError => {
                if (requestError.name !== 'AbortError') {
                    setAiPending(false);
                    setAiError(requestError.message || t.gettext('AI analysis could not be started.'));
                }
            });
    };

    const today = new Date();
    const invalidRange = dateFrom && dateTo && dateFrom > dateTo;
    const rows = report && Array.isArray(report.dailyData) ? report.dailyData : [];

    const toggleDay = date => {
        setExpandedDays(current => ({...current, [date]: !current[date]}));
    };
    const aiResult = aiSession && aiSession.result ? aiSession.result : {};
    const dataQuality = aiResult.dataQuality || {};
    const technicalAnalysis = parseObject(
        aiResult.technicalAnalysis || aiResult.technical_analysis || dataQuality.technicalAnalysis || dataQuality
    );
    const irrigation = aiResult.irrigation || {};
    const aiStatus = aiSession && aiSession.status ? aiSession.status : '';
    const aiStatusLabel = aiStatus || t.gettext('Not started');
    const aiButtonText = aiPending
        ? t.gettext('AI analysis running...')
        : aiSession
            ? t.gettext('Run AI analysis again')
            : t.gettext('Run AI analysis');

    return (
        <div className="userModalBlur">
            <div className="userModal drainWaterHistoryModal">
                <div className="userModalHeader">
                    <div className="userModalTitleContainer">
                        <button type="button" className="drainHistoryBackButton" onClick={onBack}
                                aria-label={t.gettext('Back')}>
                            <span className="material-icons">arrow_back</span>
                        </button>
                        <div className="userModalTitle">{t.gettext('Drain Water Station History')}</div>
                    </div>
                    <div className="userModalCloseButtonHolder">
                        <span className="material-icons userModalCloseButton" onClick={onClose}>cancel</span>
                    </div>
                </div>

                <div className="drainWaterHistoryContent">
                    <div className="drainHistoryStationName">
                        <span>{t.gettext('Station')}</span>
                        <strong>{report?.stationName || station.name}</strong>
                    </div>

                    <div className="drainHistoryFilters">
                        <label>
                            <span>{t.gettext('From')}</span>
                            <DatePicker selected={dateFrom} onChange={setDateFrom} maxDate={dateTo || today}
                                        dateFormat="dd/MM/yyyy" placeholderText={t.gettext('Select date')}/>
                        </label>
                        <label>
                            <span>{t.gettext('To')}</span>
                            <DatePicker selected={dateTo} onChange={setDateTo} minDate={dateFrom}
                                        maxDate={today} dateFormat="dd/MM/yyyy"
                                        placeholderText={t.gettext('Select date')}/>
                        </label>
                    </div>

                    {invalidRange && <div className="drainHistoryError">
                        {t.gettext('Date from cannot be later than date to.')}
                    </div>}
                    {error && <div className="drainHistoryError" role="alert">{error}</div>}


                    <div className="drainAiPanel">
                        <div className="drainAiHeader">
                            <div>
                                <div className="drainAiTitle">{t.gettext('AI station analysis')}</div>
                                <div className="drainAiSubtitle">
                                    {t.gettext('Uses recent drain station data to validate data quality and irrigation behavior.')}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="userAddButton drainAiButton"
                                onClick={startAiAnalysis}
                                disabled={aiPending || invalidRange}
                            >
                                {aiButtonText}
                            </button>
                        </div>

                        <div className="drainAiStatusRow">
                            <span className={`drainAiStatus drainAiStatus--${aiStatus.toLowerCase() || 'idle'}`}>
                                {aiStatusLabel}
                            </span>
                            {aiSession && aiSession.sessionId && (
                                <span className="drainAiSession">{t.gettext('Session')}: {aiSession.sessionId}</span>
                            )}
                        </div>

                        {aiPending && <div className="drainAiPending"><Spinner size={60}/></div>}
                        {aiError && <div className="drainHistoryError" role="alert">{aiError}</div>}
                        {aiSession && aiSession.status === 'FAILED' && aiSession.message && (
                            <div className="drainHistoryError" role="alert">{aiSession.message}</div>
                        )}

                        {aiSession && aiSession.status === 'COMPLETED' && (
                            <div className="drainAiResult">
                                <div className="drainAiSummaryGrid">
                                    <div className="drainAiSummaryCard">
                                        <span>{t.gettext('Overall status')}</span>
                                        <strong>{displayValue(aiResult.overallStatus)}</strong>
                                    </div>
                                    <div className="drainAiSummaryCard">
                                        <span>{t.gettext('Data quality')}</span>
                                        <strong>{displayValue(dataQuality.dataQualityStatus)}</strong>
                                        {dataQuality.dataQualityScore !== undefined && <small>{dataQuality.dataQualityScore}/100</small>}
                                    </div>
                                    <div className="drainAiSummaryCard">
                                        <span>{t.gettext('Irrigation')}</span>
                                        <strong>{displayValue(irrigation.irrigationStatus)}</strong>
                                        {irrigation.score !== undefined && <small>{irrigation.score}/100</small>}
                                    </div>
                                </div>

                                {dataQuality.summary && (
                                    <section className="drainAiSection">
                                        <h4>{t.gettext('Data quality summary')}</h4>
                                        <p>{dataQuality.summary}</p>
                                    </section>
                                )}

                                {irrigation.summary && (
                                    <section className="drainAiSection">
                                        <h4>{t.gettext('Irrigation summary')}</h4>
                                        <p>{irrigation.summary}</p>
                                    </section>
                                )}

                                <section className="drainAiSection">
                                    <h4>{t.gettext('Issues')}</h4>
                                    {renderIssues(technicalAnalysis.issues, t.gettext('No issues reported.'), t)}
                                </section>

                                <section className="drainAiSection">
                                    <h4>{t.gettext('Recommended actions')}</h4>
                                    {renderList(dataQuality.recommendedActions || irrigation.recommendations, t.gettext('No recommendations reported.'))}
                                </section>

                                {renderAssessment(technicalAnalysis.sensorAssessment, t)}
                            </div>
                        )}
                    </div>

                    <div className="drainHistoryTableWrapper">
                        {pending ? (
                            <div className="drainHistorySpinner"><Spinner size={120}/></div>
                        ) : (
                            <table className="drainHistoryTable">
                                <thead>
                                <tr>
                                    <th>{t.gettext('Date')}</th>
                                    <th>{t.gettext('Daily IN')}</th>
                                    <th>{t.gettext('Daily IN (raw)')}</th>
                                    <th>{t.gettext('Daily OUT')}</th>
                                    <th>{t.gettext('Percentage')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rows.map(row => {
                                    const cycles = Array.isArray(row.dayCycles) ? row.dayCycles : [];
                                    const hasWarning = cycles.some(cycleHasWarning);
                                    const expanded = Boolean(expandedDays[row.date]);

                                    return (
                                        <React.Fragment key={row.date}>
                                            <tr
                                                className={`drainHistoryDayRow${hasWarning ? ' drainHistoryRow--warning' : ''}`}
                                                onClick={() => toggleDay(row.date)}
                                                onKeyDown={event => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        toggleDay(row.date);
                                                    }
                                                }}
                                                tabIndex={0}
                                                aria-expanded={expanded}
                                            >
                                                <td>
                                                    <span className="material-icons drainHistoryExpandIcon">
                                                        {expanded ? 'expand_less' : 'expand_more'}
                                                    </span>
                                                    {row.date}
                                                </td>
                                                <td>{displayValue(row.incomingVolume)}</td>
                                                <td>{displayValue(row.incomingVolumeRaw)}</td>
                                                <td>{displayValue(row.outgoingVolume)}</td>
                                                <td>{displayValue(row.percentage)}</td>
                                            </tr>
                                            {expanded && (
                                                <tr className="drainCycleDetailsRow">
                                                    <td colSpan="5">
                                                        {cycles.length ? (
                                                            <table className="drainCyclesTable">
                                                                <thead>
                                                                <tr>
                                                                    <th>{t.gettext('#')}</th>
                                                                    <th>{t.gettext('Start')}</th>
                                                                    <th>{t.gettext('End')}</th>
                                                                    <th>{t.gettext('IN')}</th>
                                                                    <th>{t.gettext('IN (raw)')}</th>
                                                                    <th>{t.gettext('OUT')}</th>
                                                                    <th className="drainCycleTimingHeader">
                                                                        <CycleTimingHeaderIcon
                                                                            type="startsBefore"
                                                                            label={t.gettext('OUT started before IN')}
                                                                        />
                                                                    </th>
                                                                    <th className="drainCycleTimingHeader">
                                                                        <CycleTimingHeaderIcon
                                                                            type="finishesBefore"
                                                                            label={t.gettext('OUT finished before IN')}
                                                                        />
                                                                    </th>
                                                                </tr>
                                                                </thead>
                                                                <tbody>
                                                                {cycles.map((cycle, index) => {
                                                                    const startedBefore = cycleStartedBeforeIn(cycle);
                                                                    const finishedBefore = Boolean(cycle.outCycleFinishedBeforeInCycle);
                                                                    return (
                                                                        <tr
                                                                            key={cycle.measurementId || `${row.date}-${cycle.cycleNr || index}`}
                                                                            className={cycleHasWarning(cycle) ? 'drainHistoryRow--warning' : ''}
                                                                        >
                                                                            <td>{displayValue(cycle.cycleNr)}</td>
                                                                            <td>{formatCycleTime(cycle.cycleStartTime)}</td>
                                                                            <td>{formatCycleTime(cycle.cycleEndTime)}</td>
                                                                            <td>{displayValue(cycle.incomingVolume)}</td>
                                                                            <td>{displayValue(cycle.incomingVolumeRaw)}</td>
                                                                            <td>{displayValue(cycle.outgoingVolume)}</td>
                                                                            <td><CycleFlagIcon warning={startedBefore} label={startedBefore ? t.gettext('Warning') : t.gettext('OK')}/></td>
                                                                            <td><CycleFlagIcon warning={finishedBefore} label={finishedBefore ? t.gettext('Warning') : t.gettext('OK')}/></td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                                </tbody>
                                                            </table>
                                                        ) : (
                                                            <div className="drainCyclesEmpty">{t.gettext('No cycles for this day.')}</div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                {!rows.length && !error && (
                                    <tr><td colSpan="5" className="drainHistoryEmpty">
                                        {t.gettext('No data for the selected date range.')}
                                    </td></tr>
                                )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DrainWaterStationHistory;
